/**
 * The fictional organisation every mock endpoint describes. Generated once,
 * deterministically, so a staff member, site or client is the same entity on
 * every page (People, Clients, Board, CEO report, the assistant).
 *
 * Community names are real places on the NPY Lands; every PERSON is invented.
 * Clients never get names — like the real dashboard they are shown by code.
 */
import { DATA_START, addDays, addMonths, now, quarterOf, ym, ymd } from './clock';
import { rng } from './rng';

export const ORG = {
    name: "NPY Women's Council",
    shortName: 'NPY',
    abn: '00 000 000 000',
    domain: 'demo.npywc.example',
    headOffice: 'Mparntwe (Alice Springs)',
};

export interface Site {
    name: string;
    region: 'NT' | 'SA' | 'WA';
    kind: 'office' | 'community' | 'hub';
}

/** Service delivery sites: head office plus communities across the tri-state NPY region. */
export const SITES: Site[] = [
    { name: 'Mparntwe (Alice Springs) Office', region: 'NT', kind: 'office' },
    { name: 'Mutitjulu', region: 'NT', kind: 'community' },
    { name: 'Imanpa', region: 'NT', kind: 'community' },
    { name: 'Kaltukatjara (Docker River)', region: 'NT', kind: 'community' },
    { name: 'Aputula (Finke)', region: 'NT', kind: 'community' },
    { name: 'Pukatja (Ernabella)', region: 'SA', kind: 'hub' },
    { name: 'Amata', region: 'SA', kind: 'community' },
    { name: 'Pipalyatjara', region: 'SA', kind: 'community' },
    { name: 'Kaltjiti (Fregon)', region: 'SA', kind: 'community' },
    { name: 'Iwantja (Indulkana)', region: 'SA', kind: 'community' },
    { name: 'Mimili', region: 'SA', kind: 'community' },
    { name: 'Warakurna', region: 'WA', kind: 'hub' },
    { name: 'Papulankutja (Blackstone)', region: 'WA', kind: 'community' },
    { name: 'Irrunytju (Wingellina)', region: 'WA', kind: 'community' },
    { name: 'Wanarn', region: 'WA', kind: 'community' },
];

export const SITE_NAMES = SITES.map(s => s.name);
export const COMMUNITY_NAMES = SITES.filter(s => s.kind !== 'office').map(s => s.name);

/** Programs. "Service areas" in goals/clients, cost centres in finance, departments in HR. */
export const PROGRAMS = [
    'Disability Services (NDIS)',
    'Aged Care',
    'Ngangkari Program',
    'Child Nutrition',
    'Youth Services',
    'Domestic & Family Violence',
    'Tjanpi Desert Weavers',
    'Corporate Services',
] as const;
export type Program = (typeof PROGRAMS)[number];

/** Programs that deliver individual client support (the Clients pages). */
export const CLIENT_PROGRAMS: Program[] = ['Disability Services (NDIS)', 'Aged Care', 'Ngangkari Program', 'Youth Services'];

const FIRST = [
    'Alice', 'Amelia', 'Anna', 'Bridget', 'Caitlin', 'Chloe', 'Claire', 'Danielle', 'Ella', 'Emily', 'Fiona', 'Grace', 'Hannah',
    'Isabel', 'Jade', 'Jessica', 'Julia', 'Kate', 'Laura', 'Leah', 'Lucy', 'Maddie', 'Maya', 'Megan', 'Mia', 'Naomi', 'Nina',
    'Olivia', 'Priya', 'Rachel', 'Rebecca', 'Rosa', 'Ruby', 'Sarah', 'Sophie', 'Tahlia', 'Tara', 'Zoe', 'Aaron', 'Ben',
    'Callum', 'Daniel', 'Ethan', 'Finn', 'Harry', 'Isaac', 'Jack', 'James', 'Josh', 'Liam', 'Luke', 'Marcus', 'Nathan',
    'Oliver', 'Patrick', 'Ryan', 'Sam', 'Thomas', 'Tom', 'Will', 'Aisha', 'Mei', 'Ana', 'Sione', 'Rangi', 'Deepa', 'Kiri',
];
const LAST = [
    'Abbott', 'Allen', 'Bailey', 'Barker', 'Bennett', 'Brooks', 'Burns', 'Campbell', 'Carter', 'Clarke', 'Collins', 'Cooper',
    'Davies', 'Dixon', 'Doyle', 'Edwards', 'Ellis', 'Evans', 'Fisher', 'Fletcher', 'Ford', 'Foster', 'Gibson', 'Graham',
    'Grant', 'Hall', 'Harper', 'Hayes', 'Hughes', 'Hunt', 'Jenkins', 'Kelly', 'Kennedy', 'Lane', 'Lawson', 'Lee', 'Lloyd',
    'Marsh', 'Mason', 'Morgan', 'Murray', 'Nash', 'Nguyen', "O'Brien", 'Palmer', 'Parker', 'Pearce', 'Porter', 'Quinn',
    'Reid', 'Reynolds', 'Rhodes', 'Riley', 'Russell', 'Shaw', 'Singh', 'Stewart', 'Sullivan', 'Taylor', 'Tran', 'Turner',
    'Walsh', 'Ward', 'Watson', 'Webb', 'West', 'Wilson', 'Wright', 'Young', 'Patel', 'Kaur', 'Sharma', 'Chen', 'Wong',
];

const ROLES_BY_PROGRAM: Record<Program, string[]> = {
    'Disability Services (NDIS)': ['Disability Support Worker', 'Disability Support Worker', 'Support Coordinator', 'Team Leader – Disability', 'Allied Health Assistant'],
    'Aged Care': ['Aged Care Worker', 'Aged Care Worker', 'Home Care Coordinator', 'Team Leader – Aged Care', 'Registered Nurse'],
    'Ngangkari Program': ['Ngangkari Practitioner', 'Ngangkari Program Coordinator', 'Cultural Liaison Officer'],
    'Child Nutrition': ['Nutritionist', 'Child Nutrition Worker', 'Child Nutrition Worker', 'Program Coordinator – Nutrition'],
    'Youth Services': ['Youth Worker', 'Youth Worker', 'Youth Program Coordinator', 'Diversion Officer'],
    'Domestic & Family Violence': ['DFV Case Manager', 'DFV Case Manager', 'DFV Practice Lead', 'Family Safety Worker', 'Legal Advocate'],
    'Tjanpi Desert Weavers': ['Field Officer – Tjanpi', 'Arts Coordinator', 'Retail & Gallery Assistant'],
    'Corporate Services': ['Finance Officer', 'Payroll Officer', 'HR Advisor', 'IT Support Officer', 'Fleet & Facilities Officer', 'Executive Assistant', 'Quality & Risk Advisor'],
};

/** SCHADS-style classification levels (People pages attrition-by-level). */
export const LEVELS = ['Level 1', 'Level 2', 'Level 3', 'Level 4', 'Level 5', 'Level 6', 'Level 7', 'Level 8'];

export interface Staff {
    id: number;
    ulid: string;
    name: string;
    firstName: string;
    lastName: string;
    email: string;
    position: string;
    program: Program;
    site: string;
    level: string;
    hourlyRate: number;
    employmentType: 'Full Time' | 'Part Time' | 'Casual';
    gender: 'Female' | 'Male';
    startDate: string; // YYYY-MM-DD
    endDate: string | null; // YYYY-MM-DD for leavers
    terminationReason: string | null;
    aboriginal: boolean;
}

export interface Client {
    id: number;
    code: string; // NPY1042 — clients are never named in the dashboard
    program: Program;
    site: string;
    active: boolean;
    ndisNumber: string | null;
    joined: string; // YYYY-MM-DD
}

const ulidChars = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
export function fakeUlid(seed: string): string {
    const r = rng(`ulid:${seed}`);
    let s = '01J';
    while (s.length < 26) {
        s += ulidChars[r.int(0, 31)];
    }
    return s;
}

const SENIOR = /Coordinator|Lead|Manager|Registered Nurse|Nutritionist|Advisor/;

const LEAVE_REASONS: [string, number][] = [
    ['Resignation', 10],
    ['Resignation – relocation', 4],
    ['End of contract', 3],
    ['Resignation – career change', 2],
    ['Dismissal', 1],
    ['Retirement', 1],
];

const PROGRAM_WEIGHTS: [Program, number][] = [
    ['Disability Services (NDIS)', 26],
    ['Aged Care', 18],
    ['Domestic & Family Violence', 12],
    ['Youth Services', 11],
    ['Child Nutrition', 8],
    ['Ngangkari Program', 6],
    ['Tjanpi Desert Weavers', 7],
    ['Corporate Services', 12],
];

type EmploymentType = Staff['employmentType'];

/** Role, site and SCHADS level for a new person, drawn from `r`. */
function drawRole(r: ReturnType<typeof rng>): { program: Program; position: string; site: string; senior: boolean; level: string } {
    const program = r.weighted(PROGRAM_WEIGHTS);
    // Most people are frontline; coordinators and leads are a minority.
    const roles = ROLES_BY_PROGRAM[program];
    const frontline = roles.filter(role => !SENIOR.test(role));
    const position = frontline.length && r.bool(0.85) ? r.pick(frontline) : r.pick(roles);
    const site = program === 'Corporate Services' || /Coordinator|Lead|Advisor/.test(position) && r.bool(0.6) ? SITES[0].name : r.pick(COMMUNITY_NAMES);
    const senior = SENIOR.test(position);
    const level = senior
        ? r.weighted([['Level 4', 3], ['Level 5', 3], ['Level 6', 1]] as [string, number][])
        : r.weighted([['Level 1', 3], ['Level 2', 6], ['Level 3', 7]] as [string, number][]);
    return { program, position, site, senior, level };
}

const ACTIVE_STAFF = 205;

/**
 * First month of leaver history: 15 months before the quarter the data window
 * opens in (Jul 2024), and never less than 15 months before today. Every
 * trailing-12-month turnover window any page, trend or report asks for —
 * back to the first selectable quarter's end and the CEO report's 91-day
 * trend look-back before it — is therefore fully populated with leavers.
 */
export function leaverHistoryStart(): Date {
    const a = addMonths(quarterOf(DATA_START).start, -15);
    const b = addMonths(now(), -15);
    return a < b ? a : b;
}

function buildStaff(): Staff[] {
    const r = rng('world:staff');
    const out: Staff[] = [];
    const used = new Set<string>();
    const today = now();
    const person = (
        name: [string, string],
        role: ReturnType<typeof drawRole>,
        start: Date,
        gender: Staff['gender'],
        rate: number,
        employmentType: EmploymentType,
        leaving: { endDate: string; reason: string } | null,
        aboriginal: boolean,
    ): Staff => {
        const [firstName, lastName] = name;
        const levelIdx = LEVELS.indexOf(role.level);
        return {
            id: 1000 + out.length,
            ulid: fakeUlid(`staff:${out.length}`),
            name: `${firstName} ${lastName}`,
            firstName,
            lastName,
            email: `${firstName}.${lastName}`.toLowerCase().replace(/[^a-z.]/g, '') + `@${ORG.domain}`,
            position: role.position,
            program: role.program,
            site: role.site,
            level: role.level,
            hourlyRate: Math.round((31 + levelIdx * 4.2 + rate * 3) * 100) / 100,
            employmentType,
            gender,
            startDate: ymd(start),
            endDate: leaving?.endDate ?? null,
            terminationReason: leaving?.reason ?? null,
            aboriginal,
        };
    };

    // The ~205 people employed today.
    for (let i = 0; out.length < ACTIVE_STAFF && i < 1000; i++) {
        const firstName = r.pick(FIRST);
        const lastName = r.pick(LAST);
        const name = `${firstName} ${lastName}`;
        if (used.has(name)) {
            continue;
        }
        used.add(name);
        const role = drawRole(r);
        const yearsAgo = r.weighted([[0, 3], [1, 4], [2, 4], [4, 3], [7, 2], [12, 1]] as [number, number][]);
        const start = addDays(today, -Math.round(yearsAgo * 365 + r.int(0, 300)));
        const gender = r.bool(0.82) ? 'Female' : 'Male';
        const rate = r.next();
        const type = role.senior
            ? r.weighted([['Full Time', 6], ['Part Time', 1]] as [EmploymentType, number][])
            : r.weighted([['Full Time', 5], ['Part Time', 3], ['Casual', 3]] as [EmploymentType, number][]);
        out.push(person([firstName, lastName], role, start, gender, rate, type, null, r.bool(0.38)));
    }

    // Leavers, month by month from leaverHistoryStart() to yesterday: about
    // 3–4 a month (~20% a year, a little higher after the Christmas break),
    // skewed to casual frontline roles. Each month and each leaver is seeded
    // on its own, so a past month's leavers never change as time passes and a
    // leaver appears only once their last day has come. Every leaver worked
    // at least six weeks.
    const yesterday = ymd(addDays(today, -1));
    for (let m = leaverHistoryStart(); m <= today; m = addMonths(m, 1)) {
        const key = ym(m);
        const rm = rng(`world:leavers:${key}`);
        const month = m.getMonth() + 1;
        const n = rm.int(2, 5) + (month === 1 || month === 2 ? rm.int(0, 1) : 0);
        const dim = new Date(m.getFullYear(), m.getMonth() + 1, 0).getDate();
        for (let k = 0; k < n; k++) {
            const end = new Date(m.getFullYear(), m.getMonth(), rm.int(1, dim));
            if (ymd(end) > yesterday) {
                continue;
            }
            const rl = rng(`world:leaver:${key}:${k}`);
            let first = rl.pick(FIRST);
            let last = rl.pick(LAST);
            for (let guard = 0; used.has(`${first} ${last}`) && guard < 50; guard++) {
                first = rl.pick(FIRST);
                last = rl.pick(LAST);
            }
            used.add(`${first} ${last}`);
            const role = drawRole(rl);
            const [lo, hi] = rl.weighted([[[42, 365], 5], [[366, 730], 3], [[731, 1825], 3], [[1826, 3650], 1]] as [[number, number], number][]);
            const start = addDays(end, -rl.int(lo, hi));
            const gender = rl.bool(0.82) ? 'Female' : 'Male';
            const rate = rl.next();
            const type = role.senior
                ? rl.weighted([['Full Time', 6], ['Part Time', 1]] as [EmploymentType, number][])
                : rl.weighted([['Full Time', 4], ['Part Time', 3], ['Casual', 5]] as [EmploymentType, number][]);
            out.push(person([first, last], role, start, gender, rate, type, { endDate: ymd(end), reason: rl.weighted(LEAVE_REASONS) }, rl.bool(0.36)));
        }
    }
    return out;
}

function buildClients(): Client[] {
    const r = rng('world:clients');
    const out: Client[] = [];
    const weights: [Program, number][] = [
        ['Disability Services (NDIS)', 45],
        ['Aged Care', 30],
        ['Ngangkari Program', 10],
        ['Youth Services', 15],
    ];
    for (let i = 0; i < 168; i++) {
        const program = r.weighted(weights);
        out.push({
            id: 1001 + i,
            code: `NPY${1001 + i}`,
            program,
            site: r.pick(COMMUNITY_NAMES),
            active: i < 152,
            ndisNumber: program === 'Disability Services (NDIS)' ? `43${String(r.int(1000000, 9999999))}` : null,
            joined: ymd(addDays(now(), -r.int(60, 2400))),
        });
    }
    return out;
}

export const STAFF: Staff[] = buildStaff();
export const CLIENTS: Client[] = buildClients();

export const activeStaff = (): Staff[] => STAFF.filter(s => !s.endDate);
export const activeClients = (): Client[] => CLIENTS.filter(c => c.active);

/** Staff employed at any point within [from, to]. */
export function staffEmployedBetween(from: Date, to: Date): Staff[] {
    return STAFF.filter(s => new Date(s.startDate) <= to && (!s.endDate || new Date(s.endDate) >= from));
}

/** Named leadership used by the personas, reports and the board pages. */
export const LEADERSHIP = {
    ceo: { name: 'Margaret Ellis', title: 'Chief Executive Officer' },
    cfo: { name: 'David Nguyen', title: 'Chief Financial Officer' },
    coo: { name: 'Sarah Whitfield', title: 'Chief Operating Officer' },
    hr: { name: 'Priya Sharma', title: 'People & Culture Manager' },
    care: { name: 'Rebecca Lawson', title: 'Client Services Manager' },
    chair: { name: 'Joan Reynolds', title: 'Chair of the Board' },
};
